<template>
	<div class="relative flex flex-col">
		<div class="h-full flex justify-between w-full py-3.5">
			<div class="w-4/12 flex">
				<StoryblokComponent
					v-for="blok in search"
					:key="blok._uid"
					:blok="blok"
					class="max-w-[300px] !bg-gray-super-light"
				/>
			</div>
			<div class="flex justify-center items-center">
				<NuxtLink :to="localePath(`/`)">
					<NuxtImg
						:src="siteLogo?.filename"
						:alt="siteLogo?.alt || 'logo'"
						class="logo max-w-60"
						loading="eager"
						:width="siteLogoDimensions?.width"
						:height="siteLogoDimensions?.height"
					/>
				</NuxtLink>
			</div>
			<div class="w-4/12 flex gap-4 justify-end items-center">
				<div class="flex gap-6 cursor-pointer">
					<UserIcon
						class="h-6 w-6 text-primary"
						@click="openAccountOnDesktop"
					/>
					<div class="relative cursor-pointer" @click="openWishlist">
						<HeartIcon class="h-6 w-6 text-primary" />
						<client-only>
							<span
								v-if="wishlistProducts?.ItemCount"
								class="text-[10px] font-medium text-white text-center absolute bg-secondary rounded-full w-5 h-5 leading-5 -top-2 -right-2"
							>
								{{ wishlistProducts?.ItemCount }}
							</span>
						</client-only>
					</div>
					<div class="relative cursor-pointer" @click="isSidebarOpen = true">
						<ShoppingBagIcon class="h-6 w-6 text-primary" />
						<client-only>
							<span
								v-if="itemsCount > 0"
								class="text-[10px] font-medium text-white text-center absolute bg-secondary rounded-full w-5 h-5 leading-5 -top-2 -right-2"
							>
								{{ itemsCount || "" }}
							</span>
						</client-only>
					</div>
				</div>
			</div>
		</div>
		<div class="h-full flex justify-between gap-6 pt-5 w-full text-gray-700">
			<div class="flex items-center gap-5 w-1/2">
				<!-- Norce Categories -->
				<LayoutCategoriesNavigation
					:header-mode="'default'"
					:menu-categories-norce="menuCategoriesNorce.left"
				/>
				<!-- Storyblok menu as secondary items -->
				<StoryblokComponent
					v-for="blok in menuCategories.left"
					:key="blok._uid"
					:blok="blok"
				/>
			</div>
			<div class="flex items-center justify-end gap-5 w-1/2">
				<!-- Norce Categories -->
				<LayoutCategoriesNavigation
					:header-mode="'default'"
					:menu-categories-norce="menuCategoriesNorce.right"
				/>
				<!-- Storyblok menu as secondary items -->
				<StoryblokComponent
					v-for="blok in menuCategories.right"
					:key="blok._uid"
					:blok="blok"
				/>
			</div>
		</div>
		<LayoutSideCart />
	</div>
</template>

<script setup lang="ts">
import {
	HeartIcon,
	ShoppingBagIcon,
	UserIcon
} from "@heroicons/vue/24/outline";
import { computed, inject, provide, ref } from "vue";
import { useRouter } from "nuxt/app";
import { storeToRefs } from "pinia";
import type { SharedModal } from "~/layers/base/components/Shared/SharedModal.vue";
import type {
	MenuCategories,
	MenuCategoriesNorce,
	SiteLogoDimensions
} from "~/types/components/header";
import type { Search, SiteLogo } from "~/types/storyblok/storyblok";

const { loadUser, userHasSession } = useLoadUser();

const cartStore = useCartStore();
const modal = inject<SharedModal>("modal") as SharedModal;
const { getUser } = storeToRefs(userStore());

const itemsCount = computed(() => cartStore.itemsCount());
const { wishlistProducts } = storeToRefs(useWishlistStore());

const localePath = useLocalePath();
const isSidebarOpen = ref(false);

provide("isSidebarOpen", isSidebarOpen);

interface Props {
	search: Search[] | undefined;
	siteLogo: SiteLogo | undefined;
	siteLogoDimensions: SiteLogoDimensions;
	menuCategoriesNorce: MenuCategoriesNorce;
	menuCategories: MenuCategories;
}

const {
	search,
	siteLogo,
	siteLogoDimensions,
	menuCategoriesNorce,
	menuCategories
} = defineProps<Props>();

const router = useRouter();
const openAccountOnDesktop = async () => {
	// First check if user exists in store (from localStorage persistence)
	if (getUser.value && Object.keys(getUser.value).length > 0) {
		router.push(localePath("/account"));
	} else if (!(await userHasSession())) {
		modal.open("AccountLoginForm", {
			position: "side"
		});
	} else {
		await loadUser();
		router.push(localePath("/account"));
	}
};

const openWishlist = async () => {
	modal.open("AccountWishlist", {
		position: "side"
	});
};
</script>
